import { useGetSubscription } from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Navigates to the checkout page to add or update the subscription's payment
 * method, carrying the current plan/interval/seat count so checkout submits the
 * right subscription. When a default payment method already exists it enters
 * payment_method_update mode; otherwise it's a fresh setup. This is the single
 * source of truth for the "add payment method" action — banners and the usage
 * billing page share it so the flow stays identical everywhere.
 *
 * Reads the subscription from getSubscription, falling back to /me's
 * subscription. The banners and usage gate that trigger this action derive
 * their visibility from /me, which can report a trialing subscription while
 * getSubscription is still loading or 404ing (it 404s for new orgs). Without
 * the fallback the button would silently no-op in that window.
 */
export function useStartPaymentMethodSetup() {
  const navigate = useNavigate();
  const { data: subscription } = useGetSubscription();
  const { data: userInfo } = useUserInfo();

  return useCallback(
    (options?: { redirectTo?: string; keepTrial?: boolean }) => {
      const current =
        subscription?.data.stripe_subscription ?? userInfo?.subscription;
      if (!current) {
        return;
      }

      const checkoutParams: Record<string, string> = {
        plan: current.plan_name,
        billing_cycle: current.billing_interval,
        user_count: String(current.seat_count),
        redirect_to: options?.redirectTo ?? ROUTES.SETTINGS.USAGE_BILLING,
      };

      // Only existing payment methods are "updated"; a first-time setup omits
      // the mode so checkout runs its create-subscription branch.
      if (current.default_payment_method_id) {
        checkoutParams.mode = "payment_method_update";
      }

      // keepTrial preserves remaining trial days when a card is added to a
      // still-running trial (ConfirmSetupPage then skips trial_end_now).
      // Omitted when the intent is to convert now (e.g. credits exhausted).
      if (options?.keepTrial && current.status === "trialing") {
        checkoutParams.keep_trial = "1";
      }

      navigate(route(ROUTES.BILLING.CHECKOUT, {}, checkoutParams));
    },
    [navigate, subscription, userInfo],
  );
}
